import { all, call, put, takeLatest } from "redux-saga/effects";
import * as actions from "@/store/slices/dashboard.slice";
import * as api from "@/services/dashboard";
import { PayloadAction } from "@reduxjs/toolkit";
import { IDashboard } from "@/services/dashboard/dashboard.type";
import { normalizeMetricRanges } from "@/utils/dateTime";

// ── Action type declarations ──────────────────────────────────────────────────

export interface GetDashboardDataAction extends PayloadAction<number> {
  type: "dashboard/getDashboardData";
}
export interface GetVdiTransactionDataAction extends PayloadAction<IDashboard> {
  type: "dashboard/getVdiTransactionReport";
}
export interface GetQuikpickProductAction extends PayloadAction<IDashboard> {
  type: "dashboard/getQuikpickProductReport";
}
export interface getSmartCoolerProductsCountAction extends PayloadAction<IDashboard> {
  type: "dashboard/getSmartCoolerProductReport";
}
export interface GetAlertReportAction extends PayloadAction<IDashboard> {
  type: "dashboard/getAlertReport";
}

// ── Domain types ──────────────────────────────────────────────────────────────

export type VDIType = {
  /** Real API field — single date for day, range string for week/month/year */
  range?: string;
  /** Legacy mock / old API field */
  date?: string;
  // API v2 fields
  requestReceived: string | number;
  requestInProcess: string | number;
  requestSuccess: string | number;
  requestFailed: string | number;
  processFailed: string | number;
  partnerFailed: string | number;
  // legacy fallback fields
  accepted?: string | number;
  total?: number;
  processed?: number;
};

export type AlertsType = {
  /** Real API field — single date for day, range string for week/month/year */
  range?: string;
  /** Legacy mock / old API field */
  date?: string;
  // new API fields
  receivedAlerts: string | number;
  openAlerts: string | number;
  closedAlerts: string | number;
  failedAlerts: string | number;
  processFailedAlerts: string | number;
  partnerFailedAlerts: string | number;
  // legacy fallback
  count?: number;
};

export type quikPickMasterProductsType = {
  total: number;
  cvReady: number;
};

export type smartCoolerProductsType = {
  total: number;
};

export interface IVDITransactions {
  vdiTransactions: any;
  alerts: AlertsType[];
  quikPickMasterProducts: quikPickMasterProductsType;
  smartCoolerProducts: smartCoolerProductsType;
  startDate: string;
  endDate: string;
}

export interface IApiResponse {
  success: boolean;
  message: string;
  data: IVDITransactions;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const getErrorPayload = (error: any) => {
  const apiResponse = error?.response?.data;
  return {
    message: apiResponse?.message || error?.message || "Request failed",
    statusCode: apiResponse?.statusCode || error?.response?.status || 500,
    messageDetails: apiResponse?.messageDetails || [],
    errorCode: apiResponse?.errorCode || "DASHBOARD_ERROR",
    success: false,
  };
};

/** VDI type keys in the preferred display order */
const VDI_METRIC_TYPES = [
  "mmsMarkets",
  "mmsSales",
  "mmsProducts",
  "mmsDevices",
] as const;

type VdiMetricType = typeof VDI_METRIC_TYPES[number];

// normalizeMetricRanges is now a shared util — imported from @/utils/dateTime

/**
 * Fetch metrics for one VDI type and dispatch the result immediately.
 * Called in parallel via all() so each chart becomes ready independently —
 * a slow type never blocks the others from rendering.
 */
function* fetchAndDispatchType(
  type: VdiMetricType,
  startDate: string,
  endDate: string,
  selectedTimezone: string,
  groupBy: "day" | "week" | "month" | "year",
): Generator {
  try {
    const response: any = yield call(api.fetchVdiMetrics, {
      type,
      startDate,
      endDate,
      selectedTimezone,
      groupBy,
    });
    if (response?.data?.success) {
      // API shape: { success, message, data: { type, tz, dateRange, groupBy, metrics: [...] } }
      const metrics = response.data.data?.metrics;
      const raw: VDIType[] = Array.isArray(metrics)
        ? metrics
        : Array.isArray(response.data.data)
          ? response.data.data
          : [];
      yield put(actions.getVdiTypeSuccess({ type, data: normalizeMetricRanges(raw, groupBy) }));
    } else {
      // Non-success response — clear this type's skeleton with empty data
      yield put(actions.getVdiTypeFailure({ type }));
    }
  } catch {
    // Network / parse error — must not block sibling types
    yield put(actions.getVdiTypeFailure({ type }));
  }
}

// ── Saga handlers ─────────────────────────────────────────────────────────────

/**
 * Fires 4 parallel API calls (one per VDI metric type).
 * Each call dispatches getVdiTypeSuccess / getVdiTypeFailure the instant it
 * resolves, so every chart independently transitions from skeleton to data
 * without waiting for the others.
 */
function* getVdiTransactionReport(action: GetVdiTransactionDataAction): Generator {
  try {
    const { startDate, endDate, selectedTimezone, groupBy = 'day' } = action.payload as any;

    // All 4 run in parallel; each dispatches as soon as it finishes
    yield all(
      VDI_METRIC_TYPES.map((type) =>
        call(fetchAndDispatchType, type, startDate, endDate, selectedTimezone, groupBy),
      ),
    );

    // Signal overall completion so the VDI stat-card count (VdiCount) becomes valid
    yield put(actions.getVdiTransactionDataSuccess({}));
  } catch (error: any) {
    yield put(actions.getVdiTransactionDataFailure(getErrorPayload(error)));
  }
}

function* getQuickPickProductReport(action: GetQuikpickProductAction): Generator {
  try {
    const response: any = yield call(api.fetchQuikpickProduct, action?.payload);
    if (response?.data && response.data.success) {
      yield put(actions.getQuikpickProductDataSuccess(response.data));
    } else {
      yield put(actions.getQuikpickProductDataFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getQuikpickProductDataFailure(getErrorPayload(error)));
  }
}

function* getSmartCoolerProductDataReport(action: any): Generator {
  try {
    const response: any = yield call(api.fetchSmartCoolerProduct, action.payload);
    if (response?.data?.success) {
      yield put(actions.getSmartCoolerProductDataSuccess(response?.data));
    } else {
      yield put(actions.getSmartCoolerProductDataFailure(response?.data));
    }
  } catch (error: any) {
    yield put(actions.getSmartCoolerProductDataFailure(getErrorPayload(error)));
  }
}

function* getAlertReport(action: GetAlertReportAction): Generator {
  try {
    const { startDate, endDate, selectedTimezone, groupBy = 'day' } = action.payload as any;

    const response: any = yield call(api.fetchAlertMetrics, {
      startDate,
      endDate,
      selectedTimezone,
      groupBy,
    });

    if (response?.data?.success) {
      const metrics = response.data.data?.metrics;
      const raw: AlertsType[] = Array.isArray(metrics)
        ? metrics
        : Array.isArray(response.data.data)
          ? response.data.data
          : [];
      const normalized = normalizeMetricRanges(raw, groupBy);
      yield put(
        actions.getAlertDataSuccess({
          success: true,
          message: '',
          data: { alerts: normalized } as any,
        }),
      );
    } else {
      yield put(actions.getAlertDataFailure(response?.data ?? { message: 'Alerts metrics request failed' }));
    }
  } catch (error: any) {
    yield put(actions.getAlertDataFailure(getErrorPayload(error)));
  }
}

// ── Root watcher ──────────────────────────────────────────────────────────────

function* dashboardSaga(): Generator {
  yield takeLatest(actions.getVdiTransactionData.type, getVdiTransactionReport);
  yield takeLatest(actions.getQuikpickProductData.type, getQuickPickProductReport);
  yield takeLatest(actions.getSmartCoolerProductData.type, getSmartCoolerProductDataReport);
  yield takeLatest(actions.getAlertData.type, getAlertReport);
}

export default dashboardSaga;
